/**
 * PDF Service - Handles PDF document operations
 * This service uses PDF.js for rendering and manipulating PDF documents
 */

import * as pdfjsLib from 'pdfjs-dist';
import { debounce } from 'lodash';
import {GlobalWorkerOptions} from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { API_URL } from "@/services/api.service";

// Set the worker source path (required for PDF.js)
GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * Class for handling PDF-related operations
 */
class PdfService {
    constructor() {
        // Cache for loaded documents to avoid reloading the same document
        this.documentCache = new Map();
        // Cache for individual pages
        this.pageCache = new Map();
        // Track documents being loaded to prevent duplicate loading
        this.loadingDocuments = new Map();
        // Page preloading queue
        this.preloadQueue = [];
        // Preload worker status
        this.isPreloading = false;

        // Export PDF.js annotation mode constants
        this.annotationMode = {
            DISABLE: 0,       // Don't render annotations
            ENABLE: 1,        // Render annotations
            ENABLE_FORMS: 2,  // Render annotations and forms
            ENABLE_STORAGE: 3 // Render annotations with storage support
        };

        // Create debounced versions of methods
        this.debouncedRenderPage = debounce(this.renderPage.bind(this), 150);
    }

    /**
     * Load a PDF document from URL with progressive loading
     * @param {string} url - URL of the PDF document
     * @param {boolean} useCache - Whether to use cached document if available
     * @param {number} initialPageCount - Number of pages to load initially (default: 1)
     * @returns {Promise<Object>} - PDF document object
     */
    async loadDocument(url, useCache = true, initialPageCount = 1) {
        if (!url) {
            throw new Error('未提供PDF文檔URL');
        }

        // Check cache first if useCache is true
        if (useCache && this.documentCache.has(url)) {
            console.log('Using cached PDF document:', url);
            return this.documentCache.get(url);
        }

        // Check if this document is already being loaded
        if (this.loadingDocuments.has(url)) {
            console.log('Document already loading, waiting for completion:', url);
            return this.loadingDocuments.get(url);
        }

        // Create loading promise and track it
        const loadingPromise = this._loadDocumentWithRetry(url, initialPageCount);
        this.loadingDocuments.set(url, loadingPromise);

        try {
            const pdfDoc = await loadingPromise;

            // Store in cache if useCache is true
            if (useCache) {
                this.documentCache.set(url, pdfDoc);
            }

            // Start preloading additional pages in background
            this._schedulePagePreloading(pdfDoc);

            return pdfDoc;
        } finally {
            // Remove from loading tracker when done
            this.loadingDocuments.delete(url);
        }
    }

    /**
     * Internal method to load document with retry logic
     * @param {string} url - URL of the PDF document
     * @param {number} initialPageCount - Number of pages to load initially
     * @returns {Promise<Object>} - PDF document object
     * @private
     */
    async _loadDocumentWithRetry(url, initialPageCount, maxRetries = 3) {
        let lastError;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                // Progressive loading settings
                const loadingOptions = {
                    url: url,
                    cMapUrl: 'https://unpkg.com/pdfjs-dist@2.16.105/cmaps/',
                    cMapPacked: true,
                    withCredentials: false,
                    rangeChunkSize: 65536, // 64KB chunks for streaming
                    disableAutoFetch: false, // Enable auto-fetching for continuous loading
                    disableStream: false,
                    initialData: null, // Will be populated if we have initial chunk data
                };

                // First try to get an initial chunk for faster first page display
                if (initialPageCount > 0 && attempt === 1) {
                    try {
                        const headerResponse = await fetch(url, {
                            method: 'GET',
                            headers: { Range: 'bytes=0-8192' } // First 8KB usually contains PDF header
                        });

                        if (headerResponse.ok) {
                            loadingOptions.initialData = await headerResponse.arrayBuffer();
                        }
                    } catch (e) {
                        console.warn('Failed to fetch initial chunk, continuing with normal loading', e);
                    }
                }

                const loadingTask = pdfjsLib.getDocument(loadingOptions);

                // Set timeout for loading (30 seconds, increasing for retries)
                const timeout = 30000 * Math.min(2, attempt);
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('PDF加載超時，請檢查網絡連接')), timeout);
                });

                // Race between loading and timeout
                const pdfDoc = await Promise.race([
                    loadingTask.promise,
                    timeoutPromise
                ]);

                return pdfDoc;
            } catch (error) {
                console.warn(`PDF loading attempt ${attempt} failed:`, error);
                lastError = error;

                // Only retry on network/timeout errors, not on format errors
                if (error.name === 'MissingPDFException' ||
                    error.name === 'InvalidPDFException' ||
                    error.name === 'PasswordException') {
                    break;
                }

                // Wait before retry with exponential backoff
                if (attempt < maxRetries) {
                    const backoffTime = Math.min(1000 * Math.pow(2, attempt-1), 5000);
                    await new Promise(resolve => setTimeout(resolve, backoffTime));
                }
            }
        }

        // Format user-friendly error message
        if (lastError.name === 'MissingPDFException') {
            throw new Error('找不到指定的PDF文檔');
        } else if (lastError.name === 'InvalidPDFException') {
            throw new Error('無效的PDF文檔格式');
        } else if (lastError.name === 'PasswordException') {
            throw new Error('PDF文檔受密碼保護');
        } else if (lastError.message.includes('timeout') || lastError.message.includes('超時')) {
            throw new Error('PDF文檔加載超時，請檢查網絡連接');
        } else if (lastError.message.includes('401') || lastError.message.includes('403') ||
            lastError.message.includes('access denied') || lastError.message.includes('expired')) {
            throw new Error('文檔訪問鏈接已過期，請刷新頁面重試');
        } else {
            throw new Error(`無法載入PDF文檔: ${lastError.message || '未知錯誤'}`);
        }
    }

    /**
     * Schedule background preloading of pages
     * @param {Object} pdfDoc - PDF document object
     * @private
     */
    _schedulePagePreloading(pdfDoc) {
        // Add pages to preload queue (first 5 pages)
        for (let i = 1; i <= Math.min(5, pdfDoc.numPages); i++) {
            const cacheKey = `${pdfDoc._transport.docId}_${i}`;
            if (!this.pageCache.has(cacheKey)) {
                this.preloadQueue.push({ pdfDoc, pageNumber: i });
            }
        }

        // Start preloading if not already in progress
        if (!this.isPreloading) {
            this._processPreloadQueue();
        }
    }

    /**
     * Process the preload queue
     * @private
     */
    async _processPreloadQueue() {
        if (this.preloadQueue.length === 0) {
            this.isPreloading = false;
            return;
        }

        this.isPreloading = true;

        try {
            const nextItem = this.preloadQueue.shift();
            const { pdfDoc, pageNumber } = nextItem;
            const cacheKey = `${pdfDoc._transport.docId}_${pageNumber}`;

            if (!this.pageCache.has(cacheKey)) {
                const page = await pdfDoc.getPage(pageNumber);
                this.pageCache.set(cacheKey, page);
            }
        } catch (e) {
            console.warn('Error preloading page:', e);
        } finally {
            // Continue with next item or stop if queue is empty
            if (this.preloadQueue.length > 0) {
                setTimeout(() => this._processPreloadQueue(), 50); // Small delay to not block UI
            } else {
                this.isPreloading = false;
            }
        }
    }

    /**
     * Fetch direct document URL using a token
     * @param {string} token - Document token
     * @returns {Promise<string>} - Direct URL to the document
     */
    async getDocumentUrlFromToken(token) {
        if (!token) {
            throw new Error('未提供文檔令牌');
        }

        try {
            // Make a request to get the document metadata with direct URL
            const response = await fetch(`${API_URL}/agreements/${token}`, {
                // Add cache-control headers for better caching behavior
                headers: {
                    'Cache-Control': 'max-age=3600'
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `獲取文檔URL失敗: ${response.status}`);
            }

            const data = await response.json();

            // Extract the direct URL from the response
            if (data.direct_url) {
                console.log('成功獲取S3預簽名URL');
                // Preload the document in the background for faster display
                this._backgroundPreloadDocument(data.direct_url);
                return data.direct_url;
            }

            throw new Error('響應中未找到有效的文檔URL');
        } catch (error) {
            console.error('獲取文檔URL錯誤:', error);
            throw error;
        }
    }

    /**
     * Start preloading a document in the background
     * @param {string} url - URL of the PDF document
     * @private
     */
    _backgroundPreloadDocument(url) {
        if (this.documentCache.has(url)) return;

        // Load just the first page data to get document structure
        setTimeout(() => {
            this.loadDocument(url, true, 1).catch(e => {
                console.warn('Background preload failed:', e);
            });
        }, 100);
    }

    /**
     * Render a specific page to a canvas element with enhanced options
     * @param {Object} pdfDoc - PDF document object
     * @param {number} pageNumber - Page number to render (1-based)
     * @param {HTMLCanvasElement} canvas - Canvas element to render to
     * @param {number} scale - Scale factor for rendering (default: 1.0)
     * @param {Object} options - Additional rendering options
     * @returns {Promise<Object>} - Rendered page information
     */
    // Render a specific page - Enhanced with better scaling and caching
    async renderPage(pageNumber, lowQuality = false) {
        if (!this.pdfDoc || !this.pdfLoaded) {
            console.error('未載入 PDF 文件');
            return;
        }

        const currentRenderId = ++this.renderId;

        try {
            this.isLoadingPage = true;

            const canvas = this.$refs.pdfCanvas;
            if (!canvas) {
                console.error('找不到 PDF 畫布元素');
                return;
            }

            const container = canvas.parentElement;

            console.log(`開始渲染頁面 ${pageNumber}，縮放級別: ${this.zoomLevel}, 低品質模式: ${lowQuality}`);

            // Check if render was superseded by a newer request
            if (currentRenderId !== this.renderId) {
                console.log('取消過時的渲染請求');
                return;
            }

            let page;
            // Use cached page if available
            if (this.pageCache[pageNumber]) {
                page = this.pageCache[pageNumber];
                console.log(`使用緩存的頁面 ${pageNumber}`);
            } else {
                page = await this.pdfDoc.getPage(pageNumber);
                this.pageCache[pageNumber] = page; // Store in cache
                console.log(`載入並緩存頁面 ${pageNumber}`);
            }

            // Check again if render was superseded
            if (currentRenderId !== this.renderId) {
                console.log('取消過時的渲染請求');
                return;
            }

            // Calculate optimal scale based on container width
            let baseScale = pdfService.calculateOptimalScale(page, container, 30);

            // Reduce quality for initial load
            if (lowQuality) baseScale = baseScale * 0.8;

            // Apply user zoom factor
            const finalScale = baseScale * this.zoomLevel;

            // Get the viewport with the calculated scale
            const viewport = page.getViewport({ scale: finalScale });

            // Get the pixel ratio for high-DPI rendering - use lower ratio for quick rendering
            const pixelRatio = lowQuality ? 1 : (window.devicePixelRatio || 1);

            // Set canvas backing store dimensions (actual pixels)
            canvas.width = Math.floor(viewport.width * pixelRatio);
            canvas.height = Math.floor(viewport.height * pixelRatio);

            // Set canvas display size (CSS pixels)
            canvas.style.width = `${Math.floor(viewport.width)}px`;
            canvas.style.height = `${Math.floor(viewport.height)}px`;

            // Get the canvas context with optimized settings
            const ctx = canvas.getContext('2d', {
                alpha: false,
                willReadFrequently: false // Performance hint
            });

            // Clear any previous content
            ctx.save();
            ctx.fillStyle = 'rgb(255, 255, 255)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.restore();

            // Scale the context to account for the pixel ratio
            ctx.scale(pixelRatio, pixelRatio);

            // Use the correct annotation mode from PDF service
            // Replace renderInteractiveForms with the appropriate annotationMode value
            const annotationMode = lowQuality
                ? pdfService.annotationMode.DISABLE
                : pdfService.annotationMode.ENABLE_FORMS;

            // Prepare render context with correct properties
            const renderContext = {
                canvasContext: ctx,
                viewport: viewport,
                annotationMode: annotationMode,
                enableTextLayer: !lowQuality,
                // REMOVED: renderInteractiveForms: !lowQuality
            };

            // Render the page
            console.log('開始頁面渲染操作');
            const renderTask = page.render(renderContext);

            // Store the render promise to potentially cancel it
            this.pageRenderPromise = renderTask.promise;

            await this.pageRenderPromise;

            // Final check if this render is still relevant
            if (currentRenderId !== this.renderId) {
                console.log('渲染完成但已過時，不更新狀態');
                return;
            }

            console.log('頁面渲染完成');

            this.currentPage = pageNumber;
            this.currentPageInput = pageNumber; // 保持輸入框與當前頁面同步
            this.isLoadingPage = false;

            // Clean up memory
            this.cleanupCache();

            // Preload adjacent pages after rendering current page
            if (!lowQuality) {
                this.preloadAdjacentPages(pageNumber);
            }
        } catch (err) {
            console.error('渲染頁面錯誤:', err);
            // 只有當不是取消錯誤時才顯示錯誤消息
            if (err.message !== 'Rendering cancelled' && currentRenderId === this.renderId) {
                this.handleError(`顯示文件時出錯。${err.message || '未知錯誤'}`);
            }
            this.isLoadingPage = false;
        }
    }

    /**
     * Preload adjacent pages for smoother navigation
     * @param {Object} pdfDoc - PDF document
     * @param {number} currentPage - Current page number
     * @private
     */
    _preloadAdjacentPages(pdfDoc, currentPage) {
        const pagesToPreload = [];

        // Preload next 2 pages and previous page
        if (currentPage < pdfDoc.numPages) pagesToPreload.push(currentPage + 1);
        if (currentPage < pdfDoc.numPages - 1) pagesToPreload.push(currentPage + 2);
        if (currentPage > 1) pagesToPreload.push(currentPage - 1);

        // Add to preload queue with priority
        for (const pageNum of pagesToPreload) {
            const cacheKey = `${pdfDoc._transport.docId}_${pageNum}`;
            if (!this.pageCache.has(cacheKey)) {
                // Add to front of queue for priority
                this.preloadQueue.unshift({ pdfDoc, pageNumber: pageNum });
            }
        }

        // Start preloading if not already in progress
        if (!this.isPreloading) {
            this._processPreloadQueue();
        }
    }

    /**
     * Calculate the optimal scale for a PDF page to fit a container
     * @param {Object} page - PDF page object
     * @param {HTMLElement} container - Container element
     * @param {number} padding - Padding to leave around the page (in pixels)
     * @returns {number} - Optimal scale factor
     */
    calculateOptimalScale(page, container, padding = 20) {
        if (!page || !container) {
            return 1.0;
        }

        try {
            // Get container dimensions
            const containerWidth = container.clientWidth - (padding * 2);

            // Get original viewport at scale 1.0
            const originalViewport = page.getViewport({ scale: 1.0 });

            // Calculate scale to fit width
            return containerWidth / originalViewport.width;
        } catch (error) {
            console.error('Calculate scale error:', error);
            return 1.0; // Return default scale on error
        }
    }

    /**
     * Clear the document cache to free memory
     * @param {string} url - Specific URL to clear from cache (if not provided, clear all)
     * @param {boolean} clearPages - Whether to also clear page cache
     */
    clearCache(url = null, clearPages = true) {
        if (url) {
            this.documentCache.delete(url);
            console.log('Cleared cache for:', url);

            if (clearPages && this.documentCache.has(url)) {
                const doc = this.documentCache.get(url);
                const docId = doc._transport.docId;

                // Clear related page caches
                for (const key of this.pageCache.keys()) {
                    if (key.startsWith(`${docId}_`)) {
                        this.pageCache.delete(key);
                    }
                }
            }
        } else {
            this.documentCache.clear();
            if (clearPages) {
                this.pageCache.clear();
            }
            console.log('Cleared entire PDF document cache');
        }

        // Also clear preload queue
        this.preloadQueue = [];
    }

    /**
     * Garbage collect old cached items to prevent memory leaks
     * Call this method periodically from your application
     * @param {number} maxCacheItems - Maximum number of documents to keep in cache
     */
    performGarbageCollection(maxCacheItems = 5) {
        // Keep only the most recent documents if cache grows too large
        if (this.documentCache.size > maxCacheItems) {
            // Convert to array for sorting
            const cacheEntries = Array.from(this.documentCache.entries());

            // Sort by last accessed time (you would need to track this)
            // For now, we'll just remove the first entries (oldest added)
            const itemsToRemove = cacheEntries.slice(0, this.documentCache.size - maxCacheItems);

            for (const [url] of itemsToRemove) {
                this.clearCache(url, true);
            }

            console.log(`Garbage collected ${itemsToRemove.length} PDF documents`);
        }
    }
}

// Create and export a singleton instance
const pdfService = new PdfService();
export default pdfService;